import React, { useRef, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Alert,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Print as PrintIcon,
  Assignment as AssignmentIcon,
  Remove as RemoveIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useData } from '../contexts/DataContext';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export default function InventoryPickList() {
  const navigate = useNavigate();
  const { cartItems, updateCartItemQuantity, removeFromCart, clearCart, getCartTotal } = useCart();
  const { checkoutInventoryItemForPickList, inventoryTransactions } = useData();
  const pdfRef = useRef(null);
  const [checkingOut, setCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const [currentPickListNumber, setCurrentPickListNumber] = useState(null);

  const handleQuantityChange = (itemId, newQuantity) => {
    updateCartItemQuantity(itemId, parseFloat(newQuantity) || 0);
  };

  // Generate unique pick list number
  const generatePickListNumber = () => {
    try {
      // Get all pick-list transactions to find the highest number
      const pickListTransactions = inventoryTransactions.filter(
        t => t.jobId === 'pick-list' && t.pickListNumber
      );
      
      if (pickListTransactions.length === 0) {
        return 'PL-001';
      }
      
      // Extract numbers from pick list numbers (PL-001, PL-002, etc.)
      const numbers = pickListTransactions
        .map(t => {
          const match = t.pickListNumber?.match(/PL-(\d+)/);
          return match ? parseInt(match[1]) : 0;
        })
        .filter(n => n > 0)
        .sort((a, b) => b - a);
      
      const nextNumber = numbers.length > 0 ? numbers[0] + 1 : 1;
      return `PL-${String(nextNumber).padStart(3, '0')}`;
    } catch (error) {
      console.error('Error generating pick list number:', error);
      // Fallback to timestamp-based number
      return `PL-${Date.now().toString().slice(-6)}`;
    }
  };

  const generatePDF = async () => {
    if (!pdfRef.current) return;

    if (cartItems.length === 0) {
      alert('Pick list is empty. Add items before printing.');
      return;
    }

    setCheckingOut(true);
    setCheckoutError(null);

    try {
      // Generate unique pick list number first
      const pickListNumber = generatePickListNumber();
      setCurrentPickListNumber(pickListNumber);
      
      // First, checkout all items in the pick list with the pick list number
      const checkoutPromises = cartItems.map(item => 
        checkoutInventoryItemForPickList(
          item.id, 
          item.quantity, 
          `Pick List ${pickListNumber} - ${new Date().toLocaleDateString()}`,
          pickListNumber
        )
      );
      
      await Promise.all(checkoutPromises);
      
      // Now generate the PDF
      // US Letter size
      const pageWidth = 612;
      const pageHeight = 792;
      const margin = 24; // Reduced margins
      const contentWidth = pageWidth - 2 * margin;
      const contentHeight = pageHeight - 2 * margin;

      // Set the PDF content container to letter size
      const pdfContent = pdfRef.current;
      if (!pdfContent) {
        throw new Error('PDF content element not found');
      }

      const originalStyle = {
        width: pdfContent.style.width,
        height: pdfContent.style.height,
        display: pdfContent.style.display,
        position: pdfContent.style.position,
        left: pdfContent.style.left,
        top: pdfContent.style.top,
        padding: pdfContent.style.padding,
        margin: pdfContent.style.margin,
        boxSizing: pdfContent.style.boxSizing
      };

      // Clone the element to avoid React DOM issues
      const clonedContent = pdfContent.cloneNode(true);
      clonedContent.style.width = contentWidth + 'px';
      clonedContent.style.height = contentHeight + 'px';
      clonedContent.style.display = 'block';
      clonedContent.style.position = 'absolute';
      clonedContent.style.left = '-9999px';
      clonedContent.style.top = '0';
      clonedContent.style.padding = '0';
      clonedContent.style.margin = '0';
      clonedContent.style.boxSizing = 'border-box';
      clonedContent.style.visibility = 'visible';
      document.body.appendChild(clonedContent);

      // Update pick list number in cloned content before rendering
      const numberElement = clonedContent.querySelector('[data-pick-list-number]');
      if (numberElement) {
        numberElement.textContent = pickListNumber;
      }

      // Render to canvas at scale 2 for quality
      const scale = 2;
      const canvas = await html2canvas(clonedContent, {
        scale,
        useCORS: true,
        backgroundColor: '#ffffff',
        width: contentWidth,
        height: contentHeight,
        windowWidth: contentWidth,
        windowHeight: contentHeight,
        logging: false,
        onclone: (clonedDoc) => {
          const clonedElement = clonedDoc.querySelector('[data-pdf-content]');
          if (clonedElement) {
            clonedElement.style.width = contentWidth + 'px';
            clonedElement.style.height = 'auto';
            clonedElement.style.minHeight = contentHeight + 'px';
            clonedElement.style.display = 'block';
            clonedElement.style.background = '#fff';
            clonedElement.style.padding = '24px';
            clonedElement.style.boxSizing = 'border-box';
            clonedElement.style.visibility = 'visible';
            // Ensure pick list number is set in cloned document too
            const clonedNumberElement = clonedElement.querySelector('[data-pick-list-number]');
            if (clonedNumberElement) {
              clonedNumberElement.textContent = pickListNumber;
            }
          }
        }
      });

      // Calculate scaling to fit letter size
      const actualWidth = canvas.width / scale;
      const actualHeight = canvas.height / scale;
      
      // Scale to fit content area in points (PDF uses points)
      const scaleX = contentWidth / actualWidth;
      const scaleY = contentHeight / actualHeight;
      const scaleFactor = Math.min(scaleX, scaleY, 1); // Don't scale up, only down

      // Scale dimensions for PDF (in points)
      const scaledWidth = actualWidth * scaleFactor;
      const scaledHeight = actualHeight * scaleFactor;

      // Create PDF with letter size
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: [pageWidth, pageHeight]
      });

      const imgData = canvas.toDataURL('image/jpeg', 1.0);
      
      // Center the image on the page
      const x = (pageWidth - scaledWidth) / 2;
      const y = margin;
      
      pdf.addImage(imgData, 'JPEG', x, y, scaledWidth, scaledHeight);
      
      // Save PDF with the pick list number that was generated earlier
      pdf.save(`pick-list-${pickListNumber}.pdf`);

      // Clean up cloned element
      if (clonedContent && clonedContent.parentNode === document.body) {
        document.body.removeChild(clonedContent);
      }

      // Clear the cart after successful checkout and print
      clearCart();
      
      // Show success message
      alert(`Pick List ${pickListNumber} printed! Items have been checked out from inventory.`);
      
      // Reset pick list number
      setCurrentPickListNumber(null);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setCheckoutError(error.message || 'Failed to print pick list. Please try again.');
      alert(error.message || 'Failed to print pick list. Please try again.');
      
      // Clean up cloned element in case of error
      const clonedContent = document.body.querySelector('[data-pdf-content]');
      if (clonedContent && clonedContent.parentNode === document.body) {
        try {
          document.body.removeChild(clonedContent);
        } catch (cleanupError) {
          console.error('Error cleaning up cloned content:', cleanupError);
        }
      }
    } finally {
      setCheckingOut(false);
    }
  };

  if (cartItems.length === 0) {
    return (
      <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, mb: 3 }}>
          <IconButton onClick={() => navigate('/inventory')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography 
            variant="h4"
            sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
          >
            Pick List
          </Typography>
        </Box>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <AssignmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Pick List is Empty
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Add items from inventory to create a printable pick list for your workers
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/inventory')}
          >
            Browse Inventory
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        mb: 3,
        gap: { xs: 2, sm: 0 }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
          <IconButton onClick={() => navigate('/inventory')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography 
            variant="h4"
            sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
          >
            Pick List
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ({cartItems.length} {cartItems.length === 1 ? 'item' : 'items'})
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant="contained"
            startIcon={checkingOut ? <CircularProgress size={20} color="inherit" /> : <PrintIcon />}
            onClick={generatePDF}
            disabled={checkingOut || cartItems.length === 0}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {checkingOut ? 'Checking Out Items...' : 'Print PDF & Check Out'}
          </Button>
        </Box>
      </Box>

      {checkoutError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCheckoutError(null)}>
          {checkoutError}
        </Alert>
      )}

      <Box sx={{ width: '100%', overflowX: 'auto', mb: 3 }}>
        <TableContainer component={Paper} sx={{ minWidth: 700 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}><strong>Item</strong></TableCell>
              <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', sm: 'table-cell' } }}><strong>SKU</strong></TableCell>
              <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}><strong>Location</strong></TableCell>
              <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}><strong>Quantity</strong></TableCell>
              <TableCell align="right" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cartItems.map((item) => (
              <TableRow key={item.id}>
                <TableCell>{item.name}</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{item.sku || '-'}</TableCell>
                <TableCell>
                  {item.location ? (
                    <Typography variant="body2" fontWeight="medium" color="primary">
                      {item.location}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No location
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                      disabled={item.quantity <= 1}
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                    <TextField
                      type="number"
                      value={item.quantity}
                      onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                      inputProps={{ min: 1, step: 1 }}
                      sx={{ width: 80 }}
                      size="small"
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                      disabled={item.quantity >= item.available}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                    {item.unit && (
                      <Typography variant="body2" color="text.secondary">
                        {item.unit}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => removeFromCart(item.id)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-end' }, mb: 3 }}>
        <Paper sx={{ p: 2, minWidth: { xs: '100%', sm: 300 }, maxWidth: { xs: '100%', sm: 400 } }}>
          <Typography variant="h6" gutterBottom>
            Summary
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body1">Total Items:</Typography>
            <Typography variant="body1" fontWeight="medium">
              {cartItems.reduce((sum, item) => sum + item.quantity, 0)}
            </Typography>
          </Box>
        </Paper>
      </Box>

      {/* PDF Content (Hidden) */}
      <div
        ref={pdfRef}
        data-pdf-content
        style={{
          position: 'absolute',
          left: '-9999px',
          width: '564px', // 612 - 2*24 (letter size with reduced margins)
          minHeight: '744px', // 792 - 2*24
          backgroundColor: '#ffffff',
          padding: '24px',
          fontFamily: 'Arial, sans-serif',
          boxSizing: 'border-box',
          color: '#000000'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
          <h1 style={{ margin: 0, color: '#1976d2', fontWeight: 'bold', fontSize: '24px' }}>
            MATERIALS & TOOLS PICK LIST
          </h1>
          <div style={{ textAlign: 'right' }}>
            <p style={{ margin: '0 0 4px 0', color: '#1976d2', fontSize: '18px', fontWeight: 'bold' }}>
              Pick List #: <span data-pick-list-number>{currentPickListNumber || '---'}</span>
            </p>
            <p style={{ margin: 0, color: '#666', fontSize: '11px' }}>
              {new Date().toLocaleDateString()}
            </p>
          </div>
        </div>
        <p style={{ marginBottom: '24px', color: '#666', fontSize: '12px' }}>
          Generated: {new Date().toLocaleString()}
        </p>

        <hr style={{ marginBottom: '20px', border: 'none', borderTop: '1px solid #ddd' }} />

        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
          <thead>
            <tr style={{ backgroundColor: '#1976d2', borderBottom: '2px solid #ddd' }}>
              <th style={{ padding: '10px 8px', textAlign: 'center', color: '#fff', fontSize: 11, fontWeight: 'bold', width: '40px' }}>#</th>
              <th style={{ padding: '10px 8px', textAlign: 'left', color: '#fff', fontSize: 11, fontWeight: 'bold' }}>Item Name</th>
              <th style={{ padding: '10px 8px', textAlign: 'left', color: '#fff', fontSize: 11, fontWeight: 'bold' }}>SKU</th>
              <th style={{ padding: '10px 8px', textAlign: 'left', color: '#fff', fontSize: 11, fontWeight: 'bold' }}>Location</th>
              <th style={{ padding: '10px 8px', textAlign: 'center', color: '#fff', fontSize: 11, fontWeight: 'bold' }}>Quantity</th>
            </tr>
          </thead>
          <tbody>
            {cartItems.map((item, index) => (
              <tr key={item.id} style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ padding: '10px 8px', fontSize: 11 }}>{index + 1}</td>
                <td style={{ padding: '10px 8px', fontSize: 11 }}>{item.name || '-'}</td>
                <td style={{ padding: '10px 8px', fontSize: 11, color: '#666' }}>
                  {item.sku || '-'}
                </td>
                <td style={{ padding: '10px 8px', fontSize: 11, fontWeight: 'bold', color: '#1976d2' }}>
                  {item.location || 'No location'}
                </td>
                <td style={{ padding: '10px 8px', textAlign: 'center', fontSize: 11, fontWeight: 'bold' }}>
                  {item.quantity || 0} {item.unit || ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <hr style={{ marginTop: '20px', marginBottom: '20px', border: 'none', borderTop: '1px solid #ddd' }} />

        <div style={{ marginTop: '24px', fontSize: 11 }}>
          <p style={{ color: '#666', margin: '4px 0', fontWeight: 'bold' }}>
            Total Items: {cartItems.reduce((sum, item) => sum + (item.quantity || 0), 0)}
          </p>
        </div>

        <div style={{ marginTop: '32px', textAlign: 'center', color: '#999', fontSize: '10px' }}>
          <p style={{ margin: 0 }}>Generated by Site Sync</p>
        </div>
      </div>
    </Box>
  );
}

